import { ComponentProps, forwardRef } from 'react';
import type { Input2Size, Input2Tone } from 'v5/design-system/Input2';
import { InputGroup2, InputGroup2Input } from 'v5/design-system/InputGroup2';
import { cn } from 'v5/platform/dom/cn';

export type PercentInputProps = Omit<ComponentProps<'input'>, 'size'> & {
  tone?: Input2Tone;
  size?: Input2Size;
  invalid?: boolean;
};

/**
 * A short percentage input with a `%` suffix. The suffix is hidden from
 * screen readers: the label says what the number is ("Agency fee").
 * Behaves like a text input towards forms (id, aria-*, ref on the input).
 */
export const PercentInput = forwardRef<HTMLInputElement, PercentInputProps>(
  function PercentInput(
    {
      tone = 'regular',
      size = 'md',
      invalid = false,
      readOnly,
      className,
      ...inputProps
    },
    ref
  ) {
    return (
      <InputGroup2
        tone={tone}
        size={size}
        variant={invalid ? 'danger' : 'default'}
        aria-readonly={readOnly}
        className={cn('min-w-0', className)}
      >
        <InputGroup2Input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          readOnly={readOnly}
          className="min-w-0 text-right tabular-nums"
          {...inputProps}
        />
        {/* Not an InputGroup2Slot: slots get px-3, which pushes % away from the number. */}
        <span
          aria-hidden="true"
          className="flex items-center pr-2.5 pl-1 text-fg-muted"
        >
          %
        </span>
      </InputGroup2>
    );
  }
);
